import {createStore, del, entries, get, set, clear} from 'idb-keyval'

import {getCurrentUserLogin} from '@github-ui/client-env'
import {trackStorage} from './track-storage'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SendAnalyticsEventFunction} from './types'
export type {SendAnalyticsEventFunction} from './types'

export interface Validator {
  Check(value: unknown): boolean
  Code(): string
  Errors(value: unknown): unknown[]
}

type Props = {
  validator: Validator
  sendAnalyticsEvent: SendAnalyticsEventFunction
  storeName?: string
}

export const SIMPLE_VALIDATOR = {
  Check(value: unknown): boolean {
    return typeof value === 'object' && value !== null
  },
  Code() {
    return 'simple-validator'
  },
  Errors() {
    return []
  },
}

export const CACHE_EVENTS = {
  CACHE_HIT: 'offline_cache.cache_hit',
  CACHE_MISS: 'offline_cache.cache_miss',
  CACHE_EVICT: 'offline_cache.cache_evict',
  CACHE_INVALID: 'offline_cache.stale_structure',
  CACHE_EXPIRED: 'offline_cache.expired_item',
  CACHE_SET: 'offline_cache.cache_set',
  CACHE_TIMEOUT: 'offline_cache.cache_timeout',
  CACHE_SESSION_DISABLED: 'offline_cache.session_disabled',
  CACHE_READ_ERROR: 'offline_cache.read_error',
  CACHE_EVICT_ERROR: 'offline_cache.evict_error',
  CACHE_WRITE_ERROR: 'offline_cache.write_error',
  CLEANUP_STARTED: 'offline_cache.cleanup_started',
  CLEANUP_COMPLETED: 'offline_cache.cleanup_completed',
  CLEANUP_ERROR: 'offline_cache.cleanup_error',
  CLEANUP_ITEM_ERROR: 'offline_cache.cleanup_item_error',
  ENTRIES_READ: 'offline_cache.entries_read',
  ANY: 'offline_cache.*',
  NONE: 'offline_cache.none',
} as const

const INDEXEDDB_READ_TIMEOUT_MS = 500
const INDEXEDDB_WRITE_TIMEOUT_MS = 2000

const NOT_AVAILABLE = 'N/A'
let isIndexedDbDisabledForSession = false

function isFatalIndexedDbError(error: unknown): error is DOMException {
  if (!(error instanceof Error)) return false

  // In environments where DOMException is available, check for it explicitly
  // In other environments, don't treat errors as fatal DOMExceptions
  const isDOMException = typeof DOMException !== 'undefined' ? error instanceof DOMException : false

  if (isDOMException && 'name' in error) {
    const errorName = (error as DOMException).name
    return (
      errorName === 'SecurityError' ||
      errorName === 'UnknownError' ||
      errorName === 'AbortError' ||
      errorName === 'QuotaExceededError' ||
      errorName === 'NotFoundError' ||
      errorName === 'VersionError'
    )
  }

  return false
}

export function isIndexedDbAvailable(): boolean {
  try {
    return typeof globalThis !== 'undefined' && typeof globalThis.indexedDB !== 'undefined'
  } catch {
    return false
  }
}

class IndexedDbTimeoutError extends Error {
  constructor(operation: 'read' | 'write', key: string, timeoutMs: number) {
    super(`IndexedDB ${operation} operation timed out after ${timeoutMs}ms for key: ${key}`)
    this.name = 'IndexedDbTimeoutError'
  }
}

export type CacheEvent = (typeof CACHE_EVENTS)[keyof typeof CACHE_EVENTS]
export type ANY_CACHE_EVENT = typeof CACHE_EVENTS.ANY
export type NO_CACHE_EVENTS = typeof CACHE_EVENTS.NONE

export const DBNAME_PREFIX = 'offline_cache::'
export const CACHE_VERSION = 1
export const getDbName = () => DBNAME_PREFIX + getCurrentUserLogin()

type SafeIndexedDbPersister<TPersistedQuery> = {
  cleanupIntervalId: number | undefined
  isCleanupRunning: boolean
  getItem(key: string, attributes?: {[key: string]: unknown}): Promise<TPersistedQuery | undefined>
  setItem(
    key: string,
    value: TPersistedQuery,
    dataUpdatedAt?: number,
    ttl?: number,
    attributes?: {[key: string]: unknown},
  ): Promise<void>
  removeItem(key: string, attributes?: {[key: string]: unknown}): Promise<void>
  isBustedOrExpired(persistedQuery: TPersistedQuery): boolean
  entries(): Promise<Array<[string, TPersistedQuery]>>
  cleanupExpiredEntries(): Promise<void>
  stopPeriodicCleanup(): void
}

// Singleton cache to ensure only one persister instance per store
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const persisters = new Map<string, SafeIndexedDbPersister<any>>()

export function clearPersisterCache() {
  for (const persister of persisters.values()) {
    if (persister && typeof persister.stopPeriodicCleanup === 'function') {
      persister.stopPeriodicCleanup()
    }
  }
  persisters.clear()
  resetSessionState()
}

export function resetSessionState() {
  isIndexedDbDisabledForSession = false
}

export function createSafeIndexedDbPersister<
  TState extends {
    data: unknown | null
    dataUpdatedAt?: number
    ttl?: number
    preheatSource?: string
    cacheVersion?: number
    variables?: Record<string, unknown>
  },
  TPersistedQuery extends {state: TState; queryKey?: readonly unknown[]; queryHash: string},
>({validator, sendAnalyticsEvent, storeName = 'queries'}: Props) {
  // Create a unique key for this persister instance
  // Call validator.Code() to get the unique validator code string for this schema
  const persisterId = `${getDbName()}:${storeName}:${validator.Code()}`

  // Return existing instance if available
  const existingPersister = persisters.get(persisterId)
  if (existingPersister) {
    return existingPersister
  }

  function isExpired(persistedQuery: TPersistedQuery) {
    // Treat null/undefined records or records without state as expired (should be cleaned up)
    if (!persistedQuery || !persistedQuery.state) {
      return true
    }

    if (persistedQuery.state.dataUpdatedAt && persistedQuery.state.ttl) {
      const queryAge = Date.now() - persistedQuery.state.dataUpdatedAt
      if (queryAge > persistedQuery.state.ttl) {
        return true
      }
    }
    if (persistedQuery.state.cacheVersion !== undefined && persistedQuery.state.cacheVersion !== CACHE_VERSION) {
      return true
    }

    // Treat queries without dataUpdatedAt or ttl as never expiring
    return false
  }

  let idbStore: ReturnType<typeof createStore>
  try {
    idbStore = createStore(getDbName(), storeName)
  } catch (error) {
    if (isFatalIndexedDbError(error)) {
      isIndexedDbDisabledForSession = true
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, '', {
        reason: 'fatal_error_on_init',
        error_name: error.name,
      })
    }
  }

  function isIndexedDbDisabled(): boolean {
    return isFeatureEnabled('disable-indexdb-operations') || isIndexedDbDisabledForSession
  }

  async function withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    operation: 'read' | 'write',
    key: string,
  ): Promise<T> {
    let timeoutId: ReturnType<typeof setTimeout> | null = null

    const timeoutPromise = new Promise<T>((_, reject) => {
      timeoutId = setTimeout(() => {
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_TIMEOUT, key, {operation})
        reject(new IndexedDbTimeoutError(operation, key, timeoutMs))
      }, timeoutMs)
    })

    try {
      const result = await Promise.race([promise, timeoutPromise])
      // Clear timeout if promise resolves successfully
      if (timeoutId) clearTimeout(timeoutId)
      return result
    } catch (error) {
      // Clear timeout if promise rejects
      if (timeoutId) clearTimeout(timeoutId)
      throw error
    }
  }

  async function cleanupExpiredEntries() {
    if (persister.isCleanupRunning) return
    persister.isCleanupRunning = true
    const startTime = Date.now()
    try {
      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_STARTED, '', {})

      if (!isIndexedDbAvailable()) {
        return
      }

      const allEntries = await entries(idbStore)
      const totalEntries = allEntries.length
      let removedCount = 0

      for (const [key, value] of allEntries) {
        try {
          const item = value as TPersistedQuery
          if (isExpired(item)) {
            sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), {
              reason: 'periodic_cleanup',
            })
            // eslint-disable-next-line github/no-then
            del(key, idbStore).catch(error => {
              sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ITEM_ERROR, key.toString(), {
                error: String(error),
              })
            })
            removedCount++
          }
        } catch (error) {
          sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ITEM_ERROR, key.toString(), {
            error: String(error),
          })
        }
      }

      sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_COMPLETED, '', {
        removed_count: removedCount,
        total_entries: totalEntries,
        duration_ms: Date.now() - startTime,
      })
    } catch (error) {
      try {
        sendAnalyticsEvent(CACHE_EVENTS.CLEANUP_ERROR, '', {
          error: String(error),
        })
      } catch {
        // Swallow analytics errors to prevent unhandled rejections during cleanup
      }
    } finally {
      persister.isCleanupRunning = false
    }
  }

  function startPeriodicCleanup(intervalMs: number = 60 * 60 * 1000) {
    if (typeof window === 'undefined') return

    const scheduleCleanup = () => {
      const windowWithIdleCallback = window as Window & {requestIdleCallback?: typeof requestIdleCallback}
      if (typeof windowWithIdleCallback.requestIdleCallback === 'function') {
        windowWithIdleCallback.requestIdleCallback(() => void cleanupExpiredEntries(), {timeout: 10000})
      } else {
        setTimeout(() => void cleanupExpiredEntries(), 10000)
      }
    }

    scheduleCleanup()

    persister.cleanupIntervalId = window.setInterval(() => void cleanupExpiredEntries(), intervalMs)
  }

  function stopPeriodicCleanup() {
    if (persister.cleanupIntervalId !== undefined) {
      clearInterval(persister.cleanupIntervalId)
      persister.cleanupIntervalId = undefined
    }
  }

  const persister = {
    cleanupIntervalId: undefined as number | undefined,
    isCleanupRunning: false,
    async getItem(
      key: string,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      if (isIndexedDbDisabled() || !isIndexedDbAvailable()) {
        return
      }

      const startTime = Date.now()
      try {
        const item = await withTimeout(get<TPersistedQuery>(key, idbStore), INDEXEDDB_READ_TIMEOUT_MS, 'read', key)
        const readDurationMs = Date.now() - startTime
        if (!item?.state?.data) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_MISS, key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
          })
          return
        }

        if (isExpired(item)) {
          this.removeItem(key)
          sendAnalyticsEvent?.('offline_cache.expired_item', key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
          })
          return
        }

        const valid = validator.Check(item.state.data)
        if (!valid) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_INVALID, key.toString(), {
            ...attributes,
            errors: validator.Errors(item.state.data),
            read_duration_ms: readDurationMs,
          })
          return
        }
        const age = item.state.dataUpdatedAt ? Date.now() - item.state.dataUpdatedAt : NOT_AVAILABLE
        const ttl = item.state.ttl ?? NOT_AVAILABLE
        sendAnalyticsEvent(CACHE_EVENTS.CACHE_HIT, key.toString(), {
          ...attributes,
          age,
          ttl,
          preheat_source: item.state.preheatSource,
          read_duration_ms: readDurationMs,
        })
        return item
      } catch (error) {
        const readDurationMs = Date.now() - startTime
        if (error instanceof IndexedDbTimeoutError) {
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_MISS, key.toString(), {
            ...attributes,
            read_duration_ms: readDurationMs,
            timeout: true,
          })
          return undefined
        }
        if (isFatalIndexedDbError(error)) {
          isIndexedDbDisabledForSession = true
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, key.toString(), {
            reason: 'fatal_error',
            error_name: error.name,
            read_duration_ms: readDurationMs,
          })
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_READ_ERROR, key.toString(), {
            ...attributes,
            session_disabled: true,
            error_name: error.name,
            read_duration_ms: readDurationMs,
          })
          return undefined
        }
        throw error
      }
    },
    async setItem(
      key: string,
      value: TPersistedQuery,
      dataUpdatedAt?: number,
      ttl?: number,
      attributes?: {
        [key: string]: unknown
      },
    ) {
      if (isIndexedDbDisabled() || !isIndexedDbAvailable()) {
        return Promise.resolve()
      }

      const effectiveTtl = ttl ?? value.state.ttl
      const valueToStore = {
        ...value,
        state: {
          ...value.state,
          dataUpdatedAt: dataUpdatedAt ?? value.state.dataUpdatedAt,
          ttl: effectiveTtl,
          cacheVersion: CACHE_VERSION,
        },
      }
      trackStorage(key, sendAnalyticsEvent)
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_SET, key.toString(), {
        ...attributes,
        ttl: effectiveTtl,
      })

      try {
        await withTimeout(set(key, valueToStore, idbStore), INDEXEDDB_WRITE_TIMEOUT_MS, 'write', key)
      } catch (error) {
        if (error instanceof IndexedDbTimeoutError) {
          return
        }
        if (isFatalIndexedDbError(error)) {
          isIndexedDbDisabledForSession = true
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_SESSION_DISABLED, key.toString(), {
            reason: 'fatal_error',
            error_name: error.name,
          })
          sendAnalyticsEvent(CACHE_EVENTS.CACHE_WRITE_ERROR, key.toString(), {
            ...attributes,
            session_disabled: true,
            error_name: error.name,
          })
          return
        }
        throw error
      }
    },
    async removeItem(key: string, attributes?: {[key: string]: unknown}) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT, key.toString(), attributes)

      if (!isIndexedDbAvailable()) {
        return
      }

      return del(key, idbStore)
    },
    isBustedOrExpired(persistedQuery: TPersistedQuery) {
      return isExpired(persistedQuery)
    },
    async entries() {
      if (!isIndexedDbAvailable()) {
        return []
      }

      const startTime = Date.now()
      const things = await entries(idbStore)
      const durationMs = Date.now() - startTime
      sendAnalyticsEvent(CACHE_EVENTS.ENTRIES_READ, '', {
        duration_ms: durationMs,
        entry_count: things.length,
      })
      return things as Array<[string, TPersistedQuery]>
    },
    cleanupExpiredEntries,
    stopPeriodicCleanup,
  }

  persisters.set(persisterId, persister)

  startPeriodicCleanup()

  return persister
}

export async function clearIndexedDBPersisterData() {
  if (!isIndexedDbAvailable()) {
    return
  }

  try {
    const databases = await globalThis.indexedDB?.databases?.()
    if (!databases) return

    const clearPromises = databases
      .filter(db => db.name?.startsWith(DBNAME_PREFIX))
      .map(async db => {
        if (!db.name) return

        try {
          const openRequest = globalThis.indexedDB.open(db.name, db.version)
          const database = await new Promise<IDBDatabase>((resolve, reject) => {
            openRequest.onsuccess = () => resolve(openRequest.result)
            openRequest.onerror = () => reject(openRequest.error)
            openRequest.onupgradeneeded = () => {
              openRequest.transaction?.abort()
              reject(new Error('Unexpected upgrade needed'))
            }
          })

          const storeNames = Array.from(database.objectStoreNames)
          database.close()

          const storeClearPromises = storeNames.map(async storeName => {
            if (!db.name) return
            try {
              const store = createStore(db.name, storeName)
              await clear(store)
            } catch (error) {
              // Handle fatal errors (like SecurityError) gracefully
              if (isFatalIndexedDbError(error)) {
                return
              }
            }
          })

          await Promise.all(storeClearPromises)
        } catch (error) {
          if (isFatalIndexedDbError(error)) {
            return
          }
        }
      })

    await Promise.allSettled(clearPromises)
  } catch {
    // no-op
  }
}
